import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from 'src/logger';

const QUICKSIGHT_DOMAIN = 'quicksight.aws.amazon.com';
const AUTH_MESSAGE_DELAY_MS = 3000; // 3 seconds
const AUTH_TIMEOUT_MS = 60000; // 1 minute

export type QuickSightAuthState = 'loading' | 'needs-auth' | 'ready' | 'failed';

/**
 * Validates that a postMessage origin is a legitimate QuickSight domain.
 * Checks for exact match or valid subdomain to prevent spoofing.
 */
const isQuickSightOrigin = (origin: string): boolean => {
  try {
    const hostname = new URL(origin).hostname;
    return hostname === QUICKSIGHT_DOMAIN || hostname.endsWith(`.${QUICKSIGHT_DOMAIN}`);
  } catch {
    return false;
  }
};

/**
 * Detects QuickSight authentication state by listening for DOCUMENT_LOADED postMessage.
 *
 * - If DOCUMENT_LOADED arrives within 3s, user is already authenticated.
 * - If it doesn't arrive within 3s, user likely needs to sign in via popup.
 * - If it doesn't arrive within 1min, auth is considered failed.
 *
 * @param active - Whether the hook should be actively listening (e.g., panel is open)
 */
export const useQuickSightAuth = (active: boolean): QuickSightAuthState => {
  const [authState, setAuthState] = useState<QuickSightAuthState>('loading');
  const authTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleMessage = useCallback((event: MessageEvent) => {
    if (!isQuickSightOrigin(event.origin)) return;
    if (event.data?.eventName === 'DOCUMENT_LOADED') {
      clearTimeout(authTimerRef.current);
      clearTimeout(timeoutRef.current);
      setAuthState('ready');
      logger.info('QuickSuite: authentication confirmed, embed ready');
    }
  }, []);

  useEffect(() => {
    if (!active) return;

    // Always re-check auth on open; sessions may have expired
    setAuthState('loading');
    window.addEventListener('message', handleMessage);

    authTimerRef.current = setTimeout(() => {
      setAuthState((prev) => {
        if (prev === 'loading') {
          logger.info('QuickSuite: auth message delay reached, showing sign-in prompt');
          return 'needs-auth';
        }
        return prev;
      });
    }, AUTH_MESSAGE_DELAY_MS);

    timeoutRef.current = setTimeout(() => {
      setAuthState((prev) => {
        if (prev !== 'ready') {
          logger.warn('QuickSuite: authentication timed out');
          return 'failed';
        }
        return prev;
      });
    }, AUTH_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(authTimerRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [active, handleMessage]);

  return authState;
};
